/**
 * Objectives API service
 *
 * HTTP client for /api/v1/objectives. Provides graceful fallbacks so the
 * UI can run against the backend or against in-memory mocks during dev.
 */

import { apiClient as api } from "../../services/api/client";
import type {
  Objective,
  CreateObjectiveDTO,
  UpdateObjectiveDTO,
  ObjectivePreset,
  ObjectiveSuggestion,
  ObjectiveStatus,
  CampaignChannel,
  CampaignContent,
  TriggerGroup,
} from "./types";

type ApiResponse<T> = { success: boolean; data: T; error?: string };

function errorOf(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e.response?.data?.error || e.message || fallback;
}

export const objectivesApi = {
  /** List all objectives for a brand */
  async list(brandId: string): Promise<ApiResponse<Objective[]>> {
    try {
      const response = await api.get("/objectives", { params: { brandId } });
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: [],
        error: errorOf(err, "Error al cargar objetivos"),
      };
    }
  },

  /** Get one objective */
  async getById(id: string): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.get(`/objectives/${id}`);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Objetivo no encontrado"),
      };
    }
  },

  /** Create a draft objective from a preset */
  async create(dto: CreateObjectiveDTO): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post("/objectives", dto);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al crear el objetivo"),
      };
    }
  },

  /** Partial update */
  async update(
    id: string,
    dto: UpdateObjectiveDTO,
  ): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.patch(`/objectives/${id}`, dto);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al actualizar el objetivo"),
      };
    }
  },

  /** Delete */
  async delete(id: string): Promise<ApiResponse<null>> {
    try {
      const response = await api.delete(`/objectives/${id}`);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: null,
        error: errorOf(err, "Error al borrar el objetivo"),
      };
    }
  },

  /** Lifecycle: activate (draft|paused → active) */
  async activate(id: string): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post(`/objectives/${id}/activate`);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al activar el objetivo"),
      };
    }
  },

  /** Lifecycle: pause */
  async pause(id: string): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post(`/objectives/${id}/pause`);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al pausar el objetivo"),
      };
    }
  },

  /** Duplicate */
  async duplicate(id: string): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post(`/objectives/${id}/duplicate`);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al duplicar el objetivo"),
      };
    }
  },

  /** AI suggestion for a preset (used by Auto mode) */
  async suggest(
    preset: ObjectivePreset,
  ): Promise<ApiResponse<ObjectiveSuggestion>> {
    try {
      const response = await api.post("/objectives/suggest", { preset });
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as ObjectiveSuggestion,
        error: errorOf(err, "Error al pedir sugerencia"),
      };
    }
  },

  /* ─── Modelo de 3 tipos: lifecycle de sugerencias + revisión IA ─── */

  /** Tipo 2: aceptar una sugerencia tal cual (suggested → active) */
  async acceptSuggestion(id: string): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post(`/objectives/${id}/accept-suggestion`);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al aceptar la sugerencia"),
      };
    }
  },

  /** Tipo 2: descartar una sugerencia con motivo */
  async dismissSuggestion(
    id: string,
    reason: string,
  ): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post(`/objectives/${id}/dismiss-suggestion`, {
        reason,
      });
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al descartar la sugerencia"),
      };
    }
  },

  /** Tipo 2 → Tipo 3: convertir sugerencia en draft custom para editar */
  async editSuggestion(
    id: string,
    updates: { name?: string; messagePreview?: string },
  ): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post(
        `/objectives/${id}/edit-suggestion`,
        updates,
      );
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al editar la sugerencia"),
      };
    }
  },

  /** Tipo 3: actualizar el copy del mensaje (Marca iterando) */
  async updateMessage(
    id: string,
    payload: { name?: string; messagePreview?: string },
  ): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.patch(`/objectives/${id}/message`, payload);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al actualizar el mensaje"),
      };
    }
  },

  /** Tipo 3: enviar campaña a revisión de la IA (draft → pending_review) */
  async submitForReview(id: string): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post(`/objectives/${id}/submit-for-review`);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al enviar para revisión"),
      };
    }
  },

  /** Tipo 3: ejecutar la revisión (frontend llama tras loader de 6s) */
  async runAIReview(id: string): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post(`/objectives/${id}/ai-review`);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al ejecutar la revisión"),
      };
    }
  },

  /* ─── Multi-canal + triggers ─── */

  /** Update channels selection + per-channel content (partial) */
  async updateChannels(
    id: string,
    payload: { channels?: CampaignChannel[]; content?: CampaignContent },
  ): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.patch(`/objectives/${id}/channels`, payload);
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al actualizar canales"),
      };
    }
  },

  /** Update triggers group (replace all) */
  async updateTriggers(
    id: string,
    triggers: TriggerGroup,
  ): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.patch(`/objectives/${id}/triggers`, {
        triggers,
      });
      return response.data;
    } catch (err) {
      return {
        success: false,
        data: {} as Objective,
        error: errorOf(err, "Error al actualizar triggers"),
      };
    }
  },

  /** Demo-only: genera métricas de muestra para mostrar el dashboard completo */
  async seedMetrics(id: string): Promise<ApiResponse<Objective>> {
    try {
      const response = await api.post(`/objectives/${id}/seed-metrics`);
      return response.data;
    } catch (err) {
      return { success: false, data: {} as Objective, error: errorOf(err, "Error al generar datos") };
    }
  },

  /** Historial de ejecuciones de una campaña */
  async getRuns(id: string): Promise<ApiResponse<ObjectiveRun[]>> {
    try {
      const response = await api.get(`/objectives/${id}/runs`);
      return response.data;
    } catch (err) {
      return { success: false, data: [], error: errorOf(err, "Error al cargar historial") };
    }
  },
};

export interface ObjectiveRun {
  id: string;
  runAt: string;
  recipientsCount: number;
  deliveredCount: number;
  conversions: number;
  revenueUSD: number;
}

/** Status display metadata for the UI */
export const STATUS_META: Record<
  ObjectiveStatus,
  { label: string; classes: string; dotClass: string }
> = {
  draft: {
    label: "Borrador",
    classes: "bg-slate-50 text-slate-600 border-slate-200",
    dotClass: "bg-slate-400",
  },
  pending_review: {
    label: "En revisión IA",
    classes: "bg-blue-50 text-blue-700 border-blue-200",
    dotClass: "bg-blue-500 animate-pulse",
  },
  needs_changes: {
    label: "Necesita ajustes",
    classes: "bg-amber-50 text-amber-700 border-amber-200",
    dotClass: "bg-amber-500",
  },
  suggested: {
    label: "Sugerida por Deenex",
    classes: "bg-violet-50 text-violet-700 border-violet-200",
    dotClass: "bg-violet-500 animate-pulse",
  },
  dismissed: {
    label: "Descartada",
    classes: "bg-slate-100 text-slate-400 border-slate-200",
    dotClass: "bg-slate-300",
  },
  active: {
    label: "Activa",
    classes: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dotClass: "bg-emerald-500 animate-pulse",
  },
  paused: {
    label: "Pausada",
    classes: "bg-amber-50 text-amber-700 border-amber-200",
    dotClass: "bg-amber-500",
  },
  archived: {
    label: "Archivada",
    classes: "bg-slate-100 text-slate-500 border-slate-200",
    dotClass: "bg-slate-300",
  },
};

/** Origin display metadata para badges */
export const ORIGIN_META: Record<
  "automatic" | "suggested" | "custom",
  { label: string; classes: string; emoji: string; description: string }
> = {
  automatic: {
    label: "Automática",
    emoji: "⚡",
    classes: "bg-indigo-50 text-indigo-700 border-indigo-200",
    description: "Deenex la corre por vos, sin que tengas que hacer nada",
  },
  suggested: {
    label: "Sugerida",
    emoji: "✨",
    classes: "bg-violet-50 text-violet-700 border-violet-200",
    description: "Deenex te la propone — aceptala, editala o descartala",
  },
  custom: {
    label: "Mía",
    emoji: "🎨",
    classes: "bg-slate-100 text-slate-700 border-slate-200",
    description: "Vos la creaste y Deenex la valida antes de activarse",
  },
};
